import type { Assumptions, FoodWasteBucket, FoodWasteEstimate } from '../types';

interface Counts {
  ordered: number;
  delivered: number;
}

/**
 * Applies the assumptions to real counts, unrounded:
 * portions = ordered × rate − not collected, kg = portions × weight,
 * eur = portions × cost, co2 = kg × factor. Not floored: when more meals were
 * left uncollected than the assumed over-production, the result is negative.
 */
export function estimate(counts: Counts, assumptions: Assumptions): FoodWasteEstimate {
  const ordered = counts.ordered;
  const notCollected = counts.ordered - counts.delivered;
  const portions = ordered * (assumptions.rate / 100) - notCollected;
  const kg = portions * (assumptions.weight / 1000);

  return {
    ordered,
    notCollected,
    portions,
    kg,
    eur: portions * assumptions.cost,
    co2Kg: kg * assumptions.co2,
  };
}

/** Sums the real counts of every bucket. */
export function sumBuckets(buckets: readonly FoodWasteBucket[]): Counts & { unplanned: number } {
  return buckets.reduce(
    (sum, bucket) => ({
      ordered: sum.ordered + bucket.ordered,
      delivered: sum.delivered + bucket.delivered,
      unplanned: sum.unplanned + bucket.unplanned,
    }),
    { ordered: 0, delivered: 0, unplanned: 0 },
  );
}

/**
 * Period estimate from the summed counts. The model is linear, so this equals
 * the sum of the unrounded bucket estimates; rounding happens once, on display.
 */
export function estimateTotal(
  buckets: readonly FoodWasteBucket[],
  assumptions: Assumptions,
): FoodWasteEstimate {
  return estimate(sumBuckets(buckets), assumptions);
}

/**
 * Rounds to `decimals` places, half away from zero. Goes through toFixed(9)
 * first so float artifacts land where the exact value would (179.99999999 is
 * 180, 2.4999999999999996 is 2.5), and never returns -0.
 */
export function roundForDisplay(value: number, decimals = 0): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  const cleaned = Number((Math.abs(value) * factor).toFixed(9));
  const rounded = (Math.sign(value) * Math.round(cleaned)) / factor;
  return rounded === 0 ? 0 : rounded;
}

/** A mass (CO2e, food) in kg below 1,000 kg, in tonnes with one decimal from 1,000 kg. */
export function massForDisplay(massKg: number): { unit: 'kg' | 't'; value: number } {
  const kg = roundForDisplay(massKg);
  if (Math.abs(kg) >= 1000) {
    return { unit: 't', value: roundForDisplay(massKg / 1000, 1) };
  }
  return { unit: 'kg', value: kg };
}
